import React, { useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Plus, Search, Trash2 } from 'lucide-react';
import { useUIColors } from '../../hooks/useTheme';

const Stacked = ({ top, bottom, topBold = true, topColor, bottomColor, mutedColor }) => (
  <div style={{ lineHeight: 1.25 }}>
    <div style={{ fontWeight: topBold ? 700 : 500, fontSize: '12px', color: topColor || '#0f172a' }}>
      {top || '—'}
    </div>
    {bottom ? (
      <div style={{ fontSize: '11px', color: bottomColor || mutedColor || '#64748b', marginTop: '1px' }}>
        {bottom}
      </div>
    ) : null}
  </div>
);

const priorityPresentation = (priorityRaw, isDarkMode) => {
  const s = String(priorityRaw || '')
    .trim()
    .toLowerCase();
  const label = priorityRaw && String(priorityRaw).trim() ? String(priorityRaw).trim() : '—';
  if (s.includes('alta')) {
    return {
      label,
      bg: isDarkMode ? '#14532d' : '#dcfce7',
      color: isDarkMode ? '#bbf7d0' : '#166534',
      border: isDarkMode ? '#22c55e' : '#86efac',
      accent: '#16a34a',
    };
  }
  if (s.includes('media')) {
    return {
      label,
      bg: isDarkMode ? '#713f12' : '#fef9c3',
      color: isDarkMode ? '#fde68a' : '#854d0e',
      border: isDarkMode ? '#eab308' : '#fde68a',
      accent: '#eab308',
    };
  }
  if (s.includes('baja')) {
    return {
      label,
      bg: isDarkMode ? '#7f1d1d' : '#fee2e2',
      color: isDarkMode ? '#fca5a5' : '#991b1b',
      border: isDarkMode ? '#ef4444' : '#fecaca',
      accent: '#ef4444',
    };
  }
  return {
    label,
    bg: isDarkMode ? '#27272a' : '#f0ebe3',
    color: isDarkMode ? '#e4e4e7' : '#44403c',
    border: isDarkMode ? '#3f3f46' : '#d6d0c4',
    accent: isDarkMode ? '#52525b' : '#a8a29e',
  };
};

const TABLE_COLUMNS = [
  { key: 'Fecha', align: 'left', sortKey: 'assessed_at' },
  { key: 'Perfil', align: 'left', sortKey: 'profile_name' },
  { key: 'Contacto', align: 'left', sortKey: 'contact_name' },
  { key: 'Scores', align: 'right', sortKey: 'weighted_score' },
  { key: 'Δ vs real', align: 'right', sortKey: 'delta' },
  { key: 'Prioridad', align: 'center', sortKey: 'suggested_priority' },
];

const getSortValue = (row, sortKey) => {
  switch (sortKey) {
    case 'assessed_at':
      return String(row.assessed_at || '');
    case 'profile_name':
      return String(row.profile_name || row.profile_code || '').toLowerCase();
    case 'contact_name':
      return String(row.contact_name || '').toLowerCase();
    case 'weighted_score':
      return Number(row.weighted_score_raw);
    case 'delta':
      return Number(row.delta_raw);
    case 'suggested_priority':
      return String(row.suggested_priority || '').toLowerCase();
    default:
      return '';
  }
};

const compareRows = (a, b, sortKey, sortDir) => {
  const mult = sortDir === 'asc' ? 1 : -1;
  const va = getSortValue(a, sortKey);
  const vb = getSortValue(b, sortKey);
  if (typeof va === 'number' && typeof vb === 'number') {
    const na = Number.isFinite(va) ? va : -Infinity;
    const nb = Number.isFinite(vb) ? vb : -Infinity;
    return (na - nb) * mult;
  }
  return String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' }) * mult;
};

const formatDateLabel = (value) => {
  if (!value) return '—';
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const [y, m, d] = raw.slice(0, 10).split('-');
    return `${d}-${m}-${y}`;
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw.slice(0, 10);
  return parsed.toLocaleDateString('es-CL');
};

/**
 * Lista de rúbricas de organización: mismo look que Table3 / ContactsTable3.
 */
const OrganizationRubricsTable3 = ({
  rows = [],
  loading = false,
  error = '',
  isDarkMode = false,
  tableTitle = '4) Rúbricas',
  addButtonLabel = 'Nueva rúbrica',
  subtitleOverride,
  onAdd,
  onRowClick,
  onBulkDelete,
  addDisabled = false,
}) => {
  const ui = useUIColors();
  const bg = ui.page;
  const headerBg = ui.header;
  const border = ui.border;
  const text = ui.text;
  const muted = ui.muted;
  const rowHover = ui.rowHover;
  const padV = 9;
  const padH = 10;
  const thPadV = 8;
  const inputBg = ui.inputBg;
  const inputBorder = ui.inputBorder;

  const [searchQuery, setSearchQuery] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterProfile, setFilterProfile] = useState('');
  const [sortColumn, setSortColumn] = useState('assessed_at');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkPending, setBulkPending] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const headerSelectRef = useRef(null);

  const showBulkChrome = Boolean(onBulkDelete);
  const colCount = TABLE_COLUMNS.length + (showBulkChrome ? 1 : 0);

  const normalizedRows = useMemo(
    () =>
      (rows || []).map((row) => {
        const weighted =
          row.weighted_score == null || row.weighted_score === ''
            ? null
            : Number(row.weighted_score);
        const observed =
          row.observed_score == null || row.observed_score === ''
            ? null
            : Number(row.observed_score);
        const confidence =
          row.confidence_score == null || row.confidence_score === ''
            ? null
            : Number(row.confidence_score);
        const delta =
          weighted != null &&
          observed != null &&
          !Number.isNaN(weighted) &&
          !Number.isNaN(observed)
            ? Math.round((weighted - observed) * 100) / 100
            : null;
        return {
          ...row,
          profile_name: row.profile_name || row.profile_code || '',
          weighted_score_raw: Number.isFinite(weighted) ? weighted : NaN,
          observed_score_raw: Number.isFinite(observed) ? observed : NaN,
          confidence_score_raw: Number.isFinite(confidence) ? confidence : NaN,
          delta_raw: delta == null ? NaN : delta,
          weighted_label: weighted == null || Number.isNaN(weighted) ? '—' : weighted.toFixed(2),
          observed_label: observed == null || Number.isNaN(observed) ? '—' : observed.toFixed(1),
          confidence_label:
            confidence == null || Number.isNaN(confidence) ? '—' : confidence.toFixed(0),
          delta_label:
            delta == null
              ? '—'
              : delta > 0
                ? `+${delta.toFixed(2)}`
                : delta.toFixed(2),
        };
      }),
    [rows]
  );

  const priorityOptions = useMemo(() => {
    const map = new Map();
    normalizedRows.forEach((r) => {
      const st = String(r.suggested_priority || '').trim();
      if (st) map.set(st, st);
    });
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b, 'es'));
  }, [normalizedRows]);

  const profileOptions = useMemo(() => {
    const map = new Map();
    normalizedRows.forEach((r) => {
      const name = String(r.profile_name || '').trim();
      if (name) map.set(name, name);
    });
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b, 'es'));
  }, [normalizedRows]);

  const filteredRows = useMemo(() => {
    let list = normalizedRows;
    const q = searchQuery.trim().toLowerCase();
    const tokens = q.split(/\s+/).filter(Boolean);
    if (tokens.length) {
      list = list.filter((row) => {
        const hay = [
          row.assessed_at,
          row.profile_name,
          row.profile_code,
          row.contact_name,
          row.contact_role,
          row.suggested_priority,
          row.weighted_label,
          row.observed_label,
          row.notes,
        ]
          .filter((x) => x !== undefined && x !== null)
          .map((x) => String(x))
          .join(' ')
          .toLowerCase();
        return tokens.every((t) => hay.includes(t));
      });
    }
    if (filterPriority) {
      list = list.filter((row) => String(row.suggested_priority || '').trim() === filterPriority);
    }
    if (filterProfile) {
      list = list.filter((row) => String(row.profile_name || '').trim() === filterProfile);
    }
    return list;
  }, [normalizedRows, searchQuery, filterPriority, filterProfile]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    arr.sort((a, b) => compareRows(a, b, sortColumn, sortDir));
    return arr;
  }, [filteredRows, sortColumn, sortDir]);

  const visibleIds = useMemo(
    () => sortedRows.map((r) => Number(r.id)).filter((id) => Number.isFinite(id) && id > 0),
    [sortedRows]
  );
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
      return;
    }
    setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const toggleSelectOne = (id) => {
    const nid = Number(id);
    setSelectedIds((prev) =>
      prev.includes(nid) ? prev.filter((x) => x !== nid) : [...prev, nid]
    );
  };

  const handleBulkDelete = async () => {
    if (!onBulkDelete || selectedIds.length === 0) return;
    const selectedRows = normalizedRows.filter((r) => selectedIds.includes(Number(r.id)));
    setBulkPending(true);
    setBulkError('');
    try {
      await onBulkDelete(selectedRows);
      setSelectedIds([]);
    } catch (e) {
      setBulkError(e?.message || 'No se pudieron eliminar las rúbricas.');
    } finally {
      setBulkPending(false);
    }
  };

  const handleSortClick = (key) => {
    if (sortColumn === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(key);
      setSortDir(key === 'assessed_at' || key === 'weighted_score' ? 'desc' : 'asc');
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterPriority('');
    setFilterProfile('');
  };

  const hasActiveFilters =
    Boolean(searchQuery.trim()) || Boolean(filterPriority) || Boolean(filterProfile);

  const subtitle =
    subtitleOverride != null
      ? subtitleOverride
      : loading
        ? 'Cargando…'
        : error ||
          (hasActiveFilters
            ? `Mostrando ${sortedRows.length} de ${normalizedRows.length} evaluaciones`
            : `${normalizedRows.length} evaluación${normalizedRows.length === 1 ? '' : 'es'}`);

  const selectStyle = {
    height: '36px',
    padding: '0 10px',
    borderRadius: '8px',
    border: `1px solid ${inputBorder}`,
    background: inputBg,
    color: text,
    fontSize: '13px',
    minWidth: '140px',
    cursor: 'pointer',
    outline: 'none',
  };

  const SortIcon = ({ columnKey }) => {
    if (sortColumn !== columnKey) return <ArrowUpDown size={12} color={muted} />;
    return sortDir === 'asc' ? <ArrowUp size={12} color={text} /> : <ArrowDown size={12} color={text} />;
  };

  return (
    <div
      style={{
        border: `1px solid ${border}`,
        borderRadius: '12px',
        overflow: 'hidden',
        background: bg,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: `1px solid ${border}`,
          background: headerBg,
          flexWrap: 'wrap',
          gap: '10px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: text }}>{tableTitle}</h3>
          <p style={{ margin: 0, fontSize: '12px', color: muted }}>{subtitle}</p>
        </div>
        {onAdd ? (
          <button
            type="button"
            onClick={onAdd}
            disabled={addDisabled}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              height: '36px',
              padding: '0 14px',
              borderRadius: '8px',
              border: 'none',
              background: addDisabled ? (isDarkMode ? '#3f3f46' : '#d1d5db') : '#16a34a',
              color: '#fff',
              fontWeight: 600,
              fontSize: '13px',
              cursor: addDisabled ? 'not-allowed' : 'pointer',
              opacity: addDisabled ? 0.7 : 1,
            }}
          >
            <Plus size={16} /> {addButtonLabel}
          </button>
        ) : null}
      </div>

      <div
        style={{
          padding: '10px 16px',
          borderBottom: `1px solid ${border}`,
          background: bg,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'flex-end',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 220px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Buscar
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Search size={16} color={muted} aria-hidden />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Contacto, perfil, prioridad…"
              style={{ ...selectStyle, flex: 1, minWidth: 0, cursor: 'text' }}
            />
          </span>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '150px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Perfil
          </span>
          <select
            value={filterProfile}
            onChange={(e) => setFilterProfile(e.target.value)}
            style={selectStyle}
          >
            <option value="">Todos</option>
            {profileOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '160px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Prioridad
          </span>
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            style={selectStyle}
          >
            <option value="">Todas</option>
            {priorityOptions.map((st) => (
              <option key={st} value={st}>
                {st}
              </option>
            ))}
          </select>
        </label>
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            style={{
              height: '36px',
              padding: '0 12px',
              borderRadius: '8px',
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: text,
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            Limpiar
          </button>
        ) : null}
      </div>

      {showBulkChrome && selectedIds.length > 0 ? (
        <div
          style={{
            padding: '10px 16px',
            borderBottom: `1px solid ${border}`,
            background: isDarkMode ? '#1c1917' : '#fafaf9',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: '13px', color: text, fontWeight: 600 }}>
            {selectedIds.length} seleccionada{selectedIds.length === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            disabled={bulkPending}
            onClick={handleBulkDelete}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              height: '32px',
              padding: '0 12px',
              borderRadius: '6px',
              border: 'none',
              background: '#dc2626',
              color: '#fff',
              fontWeight: 600,
              fontSize: '12px',
              cursor: bulkPending ? 'wait' : 'pointer',
              opacity: bulkPending ? 0.7 : 1,
            }}
          >
            <Trash2 size={14} /> Eliminar
          </button>
          {bulkError ? <span style={{ fontSize: '12px', color: '#dc2626' }}>{bulkError}</span> : null}
        </div>
      ) : null}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '860px' }}>
          <thead>
            <tr style={{ background: headerBg, borderBottom: `1px solid ${border}` }}>
              {showBulkChrome ? (
                <th style={{ padding: `${thPadV}px ${padH}px`, width: 36, textAlign: 'center' }}>
                  <input
                    ref={headerSelectRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    aria-label="Seleccionar todas"
                  />
                </th>
              ) : null}
              {TABLE_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{
                    padding: `${thPadV}px ${padH}px`,
                    textAlign: col.align,
                    fontSize: '11px',
                    fontWeight: 700,
                    color: muted,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  onClick={() => handleSortClick(col.sortKey)}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent:
                        col.align === 'center' ? 'center' : col.align === 'right' ? 'flex-end' : 'flex-start',
                      gap: 4,
                      width: col.align === 'left' ? 'auto' : '100%',
                    }}
                  >
                    {col.key}
                    <SortIcon columnKey={col.sortKey} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: muted }}>
                  Cargando rúbricas…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: '#dc2626' }}>
                  {error}
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: muted }}>
                  Aún no hay rúbricas asociadas a esta organización.
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => {
                const priority = priorityPresentation(row.suggested_priority, isDarkMode);
                const selected = selectedIds.includes(Number(row.id));
                const deltaColor =
                  !Number.isFinite(row.delta_raw)
                    ? muted
                    : row.delta_raw > 0
                      ? '#16a34a'
                      : row.delta_raw < 0
                        ? '#dc2626'
                        : text;
                return (
                  <tr
                    key={row.id}
                    style={{
                      borderBottom: `1px solid ${border}`,
                      background: selected ? rowHover : bg,
                      cursor: onRowClick ? 'pointer' : 'default',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = rowHover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = selected ? rowHover : bg;
                    }}
                    onClick={() => onRowClick?.(row)}
                  >
                    {showBulkChrome ? (
                      <td
                        style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleSelectOne(row.id)}
                          aria-label={`Seleccionar rúbrica ${row.id}`}
                        />
                      </td>
                    ) : null}
                    <td style={{ padding: `${padV}px 0`, verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', alignItems: 'stretch', minHeight: '36px' }}>
                        <div
                          style={{
                            width: '3px',
                            flexShrink: 0,
                            background: priority.accent,
                            borderRadius: '0 2px 2px 0',
                          }}
                        />
                        <div style={{ padding: `2px ${padH + 2}px`, display: 'flex', alignItems: 'center' }}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onRowClick?.(row);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              margin: 0,
                              cursor: onRowClick ? 'pointer' : 'default',
                              textAlign: 'left',
                              fontWeight: 700,
                              fontSize: '12px',
                              color: '#2563eb',
                              lineHeight: 1.2,
                            }}
                          >
                            {formatDateLabel(row.assessed_at)}
                          </button>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', maxWidth: '160px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: text, lineHeight: 1.25 }}>
                        {row.profile_name || '—'}
                      </div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', maxWidth: '200px' }}>
                      <Stacked
                        top={row.contact_name || 'Sin contacto'}
                        bottom={row.contact_role || null}
                        topColor={text}
                        bottomColor={muted}
                        mutedColor={muted}
                      />
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', textAlign: 'right' }}>
                      <Stacked
                        top={row.weighted_label}
                        bottom={`Conf. ${row.confidence_label}`}
                        topColor={text}
                        bottomColor={muted}
                        mutedColor={muted}
                      />
                    </td>
                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        verticalAlign: 'middle',
                        textAlign: 'right',
                      }}
                    >
                      <Stacked
                        top={row.observed_label}
                        bottom={row.delta_label}
                        topColor={text}
                        bottomColor={deltaColor}
                        mutedColor={muted}
                      />
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', textAlign: 'center' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          maxWidth: '100%',
                          padding: '2px 8px',
                          borderRadius: '999px',
                          fontSize: '10px',
                          fontWeight: 700,
                          letterSpacing: '0.02em',
                          background: priority.bg,
                          color: priority.color,
                          border: `1px solid ${priority.border}`,
                          lineHeight: 1.25,
                        }}
                      >
                        {priority.label}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OrganizationRubricsTable3;
